import type { Theme } from "@mui/material/styles";

// Outlined-"bubble" chip colours, by workspace semantic. Each value is
// tuned per mode (resolved below) to clear WCAG AA (4.5:1) as outlined
// chip text/border on the paper backgrounds — MUI's palette "main"
// shades fail AA on these surfaces, hence the explicit values.
export interface ChipColors {
  reading: string;
  canvas: string;
  paper: string;
  optional: string;
  required: string;
  graded: string;
  not_started: string;
  in_progress: string;
  submitted: string;
  draft: string;
  returned: string;
  // Allow lookup by a dynamic hue string (row.type, row.assignmentKind…).
  [key: string]: string;
}

declare module "@mui/material/styles" {
  interface Theme {
    chips: ChipColors;
  }
  interface ThemeOptions {
    chips?: ChipColors;
  }
  // Dedicated accent for "cancel" / discard actions — a purple tuned per
  // mode to clear WCAG AAA (7:1) on background.paper, distinct from the
  // destructive error red. Kept separate from `secondary` (which is used
  // widely and only needs AA) so this can be AAA without rippling.
  interface Palette {
    cancel: Palette["primary"];
  }
  interface PaletteOptions {
    cancel?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    cancel: true;
  }
}

/**
 * sx for an outlined Chip in a given workspace semantic. Reads the
 * mode-resolved colour straight off the theme, so call sites don't pass
 * the mode around: <Chip variant="outlined" sx={chipSx(row.type)} />.
 */
export const chipSx = (hue: string) => (t: Theme) => {
  const c = t.chips[hue] ?? t.chips.optional;
  return {
    color: c,
    borderColor: c,
    "& .MuiChip-icon": { color: "inherit" },
  };
};

export const themeSettings = (mode: any) => {
  const isDark = mode === "dark";

  // Single source of truth for hyperlink color across both the
  // CssBaseline <a> rule and the MuiLink override below. Tested
  // against background.paper:
  //   dark  #5bc460 on #212121 → ~8:1   (WCAG AAA)
  //   light #0a3d18 on #e5e5e5 → ~9.7:1 (WCAG AAA, comfortably)
  // The deeper light-mode green retains the brand identity (matches
  // the dark-mode green's hue) while clearing AAA with margin —
  // important because body text near a link is often #4a4a4a-ish,
  // and a borderline link/body contrast (1.4.1) is much harder to
  // notice than a borderline link/background contrast. Update both
  // sides together if either token changes.
  const linkColor = isDark ? "#5bc460" : "#0a3d18";

  return {
    chips: {
      // Resource type
      reading: isDark ? "#9fa8da" : "#283593", // indigo
      canvas: isDark ? "#ce93d8" : "#6a1b9a", // purple
      paper: isDark ? "#4db6ac" : "#00695c", // teal
      // Assignment kind
      optional: isDark ? "#bdbdbd" : "#5f6368", // grey
      required: isDark ? "#90caf9" : "#0d47a1", // blue
      graded: isDark ? "#81c784" : "#1b5e20", // green — "this counts"
      // Per-user status
      not_started: isDark ? "#bdbdbd" : "#5f6368", // grey
      in_progress: isDark ? "#90caf9" : "#0d47a1", // blue — active
      submitted: isDark ? "#81c784" : "#1b5e20", // green — done
      draft: isDark ? "#90caf9" : "#0d47a1", // blue — working, not final
      returned: isDark ? "#ffb74d" : "#9a4f00", // amber — sent back, act
    },
    palette: {
      mode,
      contrastThreshold: 4.5,
      ...(isDark
        ? {
            primary: {
              main: "#2d2c31",
            },
            secondary: {
              main: "#a792c9",
            },
            // Cancel/discard accent — #b8a6da on #212121 ≈ 7.29:1 (AAA).
            cancel: {
              main: "#b8a6da",
            },
            // Lighter than MUI's default dark error (#f44336 ≈ 4.39:1 on the
            // paper, just under AA) so destructive button/text labels clear
            // 4.5:1 while staying unmistakably "danger" red.
            error: {
              main: "#e57373",
            },
            // "Running/active" green. Reuses the link-green hue so the
            // brand stays coherent. Tested on both surfaces it appears
            // on (the MuiAppBar override below sets the navbar color):
            //   #5bc460 on #212121 (paper)  ≈ 7.29:1 (AAA)
            //   #5bc460 on #2d2c31 (navbar) ≈ 6.27:1 (AA)
            success: {
              main: "#5bc460",
            },
            background: {
              default: "#2f2e2e",
              paper: "#212121",
            },
          }
        : {
            primary: {
              main: "#5e5c66",
            },
            secondary: {
              main: "#5e2fa8",
            },
            // Cancel/discard accent — #58269e on #e5e5e5 ≈ 7.63:1 (AAA).
            cancel: {
              main: "#58269e",
            },
            // "Running/active" green. The MuiAppBar override below paints
            // the navbar #e5e5e5 — same as paper — so one value covers
            // both surfaces:
            //   #1b5e20 on #e5e5e5 (paper & navbar) ≈ 6.25:1 (AA, AAA-large)
            // (The old hardcoded #3DED97 was 1.21:1 here — invisible.)
            success: {
              main: "#1b5e20",
            },
            background: {
              default: "#d3d3d3",
              paper: "#e5e5e5",
            },
            text: {
              secondary: "rgba(0,0,0,0.65)",
            },
          }),
    },
    typography: {
      fontFamily: ["Fira Code", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Fira Code", "sans-serif"].join(","),
        fontSize: 50,
      },
      h2: {
        fontFamily: ["Fira Code", "sans-serif"].join(","),
        fontSize: 36,
      },
      h3: {
        fontFamily: ["Fira Code", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Fira Code", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Fira Code", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Fira Code", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: isDark ? "#2d2c31" : "#e5e5e5",
            color: isDark ? "#ffffff" : "rgba(0,0,0,0.87)",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            opacity: 1,
            backgroundColor: isDark ? "#888888" : "#767676",
          },
          switchBase: {
            "&.Mui-checked + .MuiSwitch-track": {
              opacity: 1,
              backgroundColor: isDark ? "#9575cd" : "#5e5c66",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            ".MuiAppBar-root &": {
              color: "inherit",
            },
          },
          // Dark-mode primary (#2d2c31) is a chrome colour — invisible as a
          // non-filled button accent on dark surfaces (the same WCAG fail
          // the MuiLink override below documents). Render outlined/text
          // primary buttons at a readable neutral so default <Button>s
          // clear AA app-wide without per-call-site color="inherit".
          // Contained keeps its dark fill + white text, and primary.main
          // (and the chrome surfaces that reuse it) is left untouched.
          ...(isDark
            ? {
                outlinedPrimary: {
                  color: "rgba(255,255,255,0.9)",
                  borderColor: "rgba(255,255,255,0.35)",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.6)",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                },
                textPrimary: {
                  color: "rgba(255,255,255,0.9)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                },
              }
            : {}),
        },
      },
      // Toggle controls default to primary, which checked is the invisible
      // dark chrome colour. Match the (purple) Switch accent so a checked
      // box/radio is visible — purple clears the 3:1 bar for a non-text UI
      // component on the dark paper.
      MuiCheckbox: {
        styleOverrides: {
          root: isDark ? { "&.Mui-checked": { color: "#9575cd" } } : {},
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: isDark ? { "&.Mui-checked": { color: "#9575cd" } } : {},
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            ".MuiAppBar-root &": {
              color: "inherit",
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
          a {
            color: ${linkColor};
          }
        `,
      },
      // MUI's <Link> component applies color via its `color` prop
      // and ignores the CssBaseline `a` rule above. Without this
      // override, every <Link color="primary" /> (the default)
      // renders at palette.primary.main — which in dark mode is
      // ~#2d2c31 on a ~#212121 paper, effectively invisible
      // (WCAG fail). The override forces all Link instances onto
      // the same readable token as <a> and reinstates MUI's
      // accessible "underline always" default in case a caller
      // forgot to set it.
      MuiLink: {
        defaultProps: {
          underline: "always" as const,
        },
        styleOverrides: {
          root: {
            color: linkColor,
          },
        },
      },
    },
  };
};
